/**
 * Mix ducking for music / ambient buses.
 *
 * Product rules (docs/Audio-Design.md):
 * - Reveal open → deep duck (−8…−12 dB) so the Word is readable
 * - VO / proclaim-with-VO → lighter duck (−4…−6 dB)
 * - State + SFX stay up; only music/ambient are ducked
 */

import type { AudioEvent } from "./events";
import type { AudioSettings } from "./settings";

/** Design midpoint: Reveal −10 dB */
export const REVEAL_DUCK_DB = -10;
/** Light duck when spoken Word is active */
export const VOICE_DUCK_DB = -5;
/** Slight duck under silent-first proclaim (karaoke attention) */
export const PROCLAIM_DUCK_DB = -3;
/**
 * Lie claim VO under Scripture — deep duck so the Word owns the mix,
 * but not so deep that a focused listen loses the claim entirely.
 * Applied on the lieVo bus only (separate from music/ambient duck).
 */
export const LIE_VO_UNDER_SCRIPTURE_DB = -10;
/** Still drop lies a bit while mouth is busy even if Scripture VO missing */
export const LIE_VO_UNDER_PROCLAIM_DB = -7;

export function dbToLinear(db: number): number {
  return Math.pow(10, db / 20);
}

export type DuckState = {
  reveal: boolean;
  proclaiming: boolean;
  voicePlaying: boolean;
};

/**
 * Combined music/ambient multiplier from active duck reasons.
 * Deepest (most negative dB) wins.
 */
export function duckMultiplier(
  state: DuckState,
  settings: Pick<AudioSettings, "voiceEnabled">,
): number {
  let db = 0;
  if (state.reveal) db = Math.min(db, REVEAL_DUCK_DB);
  if (state.voicePlaying && settings.voiceEnabled) {
    db = Math.min(db, VOICE_DUCK_DB);
  } else if (state.proclaiming) {
    // Soft attention duck even without VO (karaoke + speech bed)
    db = Math.min(db, PROCLAIM_DUCK_DB);
  }
  return db >= 0 ? 1 : dbToLinear(db);
}

/** Time constants for gain ramps (seconds). */
export const DUCK_ATTACK_S = 0.08;
export const DUCK_RELEASE_S = 0.28;

export class DuckingController {
  private reveal = false;
  private proclaiming = false;
  private voicePlaying = false;

  getState(): DuckState {
    return {
      reveal: this.reveal,
      proclaiming: this.proclaiming,
      voicePlaying: this.voicePlaying,
    };
  }

  /** Update flags from structured Game audio events. */
  handleEvent(e: AudioEvent): void {
    switch (e.type) {
      case "reveal.open":
        this.reveal = true;
        break;
      case "reveal.close":
        this.reveal = false;
        break;
      case "proclaim.start":
        this.proclaiming = true;
        break;
      case "proclaim.end":
        this.proclaiming = false;
        this.voicePlaying = false;
        break;
      case "mission.won":
      case "mission.lost":
      case "mission.start":
        this.reveal = false;
        this.proclaiming = false;
        this.voicePlaying = false;
        break;
      case "pause":
        // Keep duck flags; beds freeze separately
        break;
      default:
        break;
    }
  }

  /** Future VoicePlayer can mark VO active independently of lock. */
  setVoicePlaying(playing: boolean): void {
    this.voicePlaying = playing;
  }

  reset(): void {
    this.reveal = false;
    this.proclaiming = false;
    this.voicePlaying = false;
  }

  multiplier(settings: Pick<AudioSettings, "voiceEnabled">): number {
    return duckMultiplier(this.getState(), settings);
  }

  /**
   * Duck multiplier for the lieVo bus only.
   * Deep under spoken Scripture; moderate under silent proclaim lock.
   */
  lieVoMultiplier(settings: Pick<AudioSettings, "voiceEnabled">): number {
    const state = this.getState();
    if (state.voicePlaying && settings.voiceEnabled) {
      return dbToLinear(LIE_VO_UNDER_SCRIPTURE_DB);
    }
    if (state.proclaiming) {
      return dbToLinear(LIE_VO_UNDER_PROCLAIM_DB);
    }
    return 1;
  }

  /** Attack when ducking deeper, release when rising. */
  rampSeconds(prevMul: number, nextMul: number): number {
    return nextMul < prevMul - 0.01 ? DUCK_ATTACK_S : DUCK_RELEASE_S;
  }
}
